import {
	createAsyncThunk,
	createSelector,
	createSlice,
} from "@reduxjs/toolkit";
import { aufmassApi } from "../services/aufmass";
import { projectMembersApi } from "../services/projectMembers";
import { selectCurrentProjectId } from "./projectDetailsSlice";

// Helper function to transform backend response to consistent payload format
const transformAufmassResponse = (response) => {
	const data = response.data || response;

	return {
		formData: data.aufmass || {
			id: null,
			name: "",
			number: "",
			date: null,
			status: "DRAFT",
		},
		invoiceData: data.invoice,
		positions: data.positions || [],
		addedGroups: data.measurements?.addedGroups || {},
		readonly: data.aufmass?.readonly || false,
		positionUnitMap: data.measurements?.positionUnitMap || {},
		carriedForwardData: data.measurements?.carriedForwardData || {},
		hierarchicalLVData: data.ui?.hierarchicalLVData || [],
	};
};

// Shared reducer function for aufmass data updates
const updateAufmassState = (state, action) => {
	state.loading = false;
	const {
		formData,
		invoiceData,
		positions,
		addedGroups,
		readonly,
		positionUnitMap,
		carriedForwardData,
		hierarchicalLVData,
	} = action.payload;

	state.formState = { ...state.formState, ...formData, readonly };
	state.invoiceData = invoiceData;
	state.positions = positions;
	state.addedGroups = addedGroups || {};
	state.positionUnitMap = positionUnitMap || {};
	state.carriedForwardData = carriedForwardData || {};
	state.hierarchicalLVData = hierarchicalLVData || [];
};

// V2-specific async thunks for API calls
export const fetchProjectAufmassListV2 = createAsyncThunk(
	"aufmassV2/fetchProjectAufmassList",
	async (projectId, { dispatch, rejectWithValue }) => {
		const queryResult = dispatch(
			aufmassApi.endpoints.getProjectAufmass.initiate(projectId),
		);
		try {
			const response = await queryResult.unwrap();
			return response;
		} catch (error) {
			return rejectWithValue(
				error.message || "Failed to fetch aufmass list",
			);
		} finally {
			queryResult.unsubscribe();
		}
	},
);

export const loadAufmassDataV2 = createAsyncThunk(
	"aufmassV2/loadAufmassData",
	async (invoiceId, { dispatch, rejectWithValue, getState }) => {
		const projectId = selectCurrentProjectId(getState());
		if (!projectId) return rejectWithValue("No project context");
		const queryResult = dispatch(
			aufmassApi.endpoints.getAufmassDetailsV2.initiate({
				projectId,
				invoiceId,
			}),
		);
		try {
			const response = await queryResult.unwrap();
			const transformed = transformAufmassResponse(response);

			// Force readonly unless the caller has project-scoped
			// invoice.manageAufmass (project admins implicitly do). Reads the
			// cached project member loaded by useProjectPermission - not the old
			// global RBAC right.
			const member = projectMembersApi.endpoints.getCurrentMember.select(
				{ projectId },
			)(getState())?.data;
			const canEdit =
				member?.isProjectAdmin === true ||
				member?.permissions?.invoice?.manageAufmass === true;
			if (!canEdit) {
				transformed.readonly = true;
			}

			return transformed;
		} catch (error) {
			return rejectWithValue(
				error?.data?.message ||
					error.message ||
					"Failed to load aufmass data",
			);
		} finally {
			queryResult.unsubscribe();
		}
	},
);

// Utility function to prepare data for submission
export const prepareAufmassV2DataForSubmission = (
	formData,
	addedGroups,
	invoiceId,
) => {
	const { selectedPositions, id, readonly, ...aufmassData } = formData;

	return {
		...aufmassData,
		invoiceId,
		positions: Object.entries(addedGroups).map(([positionId, groups]) => {
			// Calculate total quantity for each group and format the data structure properly
			const formattedGroups = groups.map((group) => {
				const totalQuantity = group.lines
					? group.lines.reduce(
							(sum, line) => sum + parseFloat(line.quantity || 0),
							0,
						)
					: 0;

				return {
					...group,
					totalQuantity,
				};
			});

			return {
				positionId,
				groups: formattedGroups,
			};
		}),
	};
};

// Initial state for V2
const initialStateV2 = {
	// Core form state
	formState: {
		id: null,
		name: "",
		number: "",
		date: new Date().toISOString().split("T")[0],
		status: "DRAFT",
		invoiceId: null,
		readonly: false,
	},

	// Invoice data
	invoiceData: null,

	// Positions and groups data
	positions: [],
	addedGroups: {},
	positionUnitMap: {},
	carriedForwardData: {},
	hierarchicalLVData: [],

	// UI state
	expandedPositions: [],
	selectedPosition: null,
	editingLine: null,

	// Modal states
	showAddLineModal: false,
	showAddGroupModal: false,
	showDeleteModal: false,
	showDeleteLineModal: false,
	showDeleteGroupModal: false,

	// Modal data
	deleteLineData: null,
	deleteGroupData: null,
	editGroupData: null,

	// Creation/Update state
	creation: {
		loading: false,
		error: null,
		success: false,
	},

	// Change tracking
	changeTracking: {
		isDirty: false,
		dirtyTimestamp: null,
		lastAutosaved: null,
	},

	// Loading states
	loading: false,
	error: null,

	// List state
	aufmassList: [],
	loadingList: false,
	errorList: null,

	// UI state
	ui: {
		autosaving: false,
		showOnlyInvalidPositions: false,
	},
};

const aufmassV2Slice = createSlice({
	name: "aufmassV2",
	initialState: initialStateV2,
	reducers: {
		// Form state management
		setFormField: (state, action) => {
			const { field, value, skipDirtyFlag = false } = action.payload;
			state.formState[field] = value;

			if (!skipDirtyFlag) {
				state.changeTracking.isDirty = true;
				state.changeTracking.dirtyTimestamp = Date.now();
			}
		},

		markFormDirty: (state) => {
			state.changeTracking.isDirty = true;
			state.changeTracking.dirtyTimestamp = Date.now();
		},

		markFormClean: (state) => {
			state.changeTracking.isDirty = false;
			state.changeTracking.dirtyTimestamp = null;
		},

		updateFormFieldV2: (state, action) => {
			const { name, value } = action.payload;
			state.formState[name] = value;
			state.changeTracking.isDirty = true;
			state.changeTracking.dirtyTimestamp = Date.now();
		},

		// UI actions
		setShowOnlyInvalidPositions: (state, action) => {
			state.ui.showOnlyInvalidPositions = action.payload;
		},

		// Position management
		toggleExpandPosition: (state, action) => {
			const positionId = action.payload;
			const index = state.expandedPositions.indexOf(positionId);

			if (index !== -1) {
				state.expandedPositions = state.expandedPositions.filter(
					(id) => id !== positionId,
				);
			} else {
				state.expandedPositions = [
					...state.expandedPositions,
					positionId,
				];
			}
		},

		// Modal management
		setAddLineModal: (state, action) => {
			const { show, position, editData } = action.payload;
			state.showAddLineModal = show;
			state.selectedPosition = position || null;
			state.editingLine = editData || null;
		},

		setAddGroupModal: (state, action) => {
			state.showAddGroupModal = action.payload.show;
			state.selectedPosition = action.payload.position || null;
			// Store edit data for the modal to use
			if (action.payload.editData) {
				state.editGroupData = action.payload.editData;
			} else if (!action.payload.show) {
				// Clear edit data when modal is closed
				state.editGroupData = null;
			}
		},

		// Groups and lines management
		setAddedGroups: (state, action) => {
			state.addedGroups = action.payload;
			state.changeTracking.isDirty = true;
			state.changeTracking.dirtyTimestamp = Date.now();
		},

		updateAddedLine: (state, action) => {
			const { lineId, groupId, positionId, updates } = action.payload;

			if (state.addedGroups[positionId]) {
				const groups = [...state.addedGroups[positionId]];
				const groupIndex = groups.findIndex(
					(group) => group.groupId === groupId,
				);

				if (groupIndex !== -1) {
					const group = { ...groups[groupIndex] };
					const lineIndex = group.lines.findIndex(
						(line) => line.id === lineId,
					);

					if (lineIndex !== -1) {
						group.lines = [...group.lines];
						group.lines[lineIndex] = {
							...group.lines[lineIndex],
							...updates,
						};

						group.totalQuantity = group.lines.reduce(
							(sum, line) => sum + parseFloat(line.quantity || 0),
							0,
						);

						groups[groupIndex] = group;
						state.addedGroups[positionId] = groups;
						state.changeTracking.isDirty = true;
						state.changeTracking.dirtyTimestamp = Date.now();
					}
				}
			}
		},

		// Delete modal actions
		setDeleteLineModal: (state, action) => {
			state.showDeleteLineModal = action.payload.show;
			state.deleteLineData = action.payload.show ? action.payload : null;
		},

		setDeleteGroupModal: (state, action) => {
			state.showDeleteGroupModal = action.payload.show;
			state.deleteGroupData = action.payload.show ? action.payload : null;
		},

		// Reset form
		resetForm: (state) => {
			return {
				...initialStateV2,
				// Preserve list state
				aufmassList: state.aufmassList,
				loadingList: state.loadingList,
				errorList: state.errorList,
			};
		},

		// Expand/collapse all positions
		expandAllPositions: (state) => {
			state.expandedPositions = Object.keys(state.positions || {});
		},

		collapseAllPositions: (state) => {
			state.expandedPositions = [];
		},

		// ── Mutation-result writers (called by components after RTK mutations resolve)

		/** Apply a raw aufmass response (transform here) to editor state. */
		applyAufmassResultRaw: (state, { payload }) => {
			updateAufmassState(state, {
				payload: transformAufmassResponse(payload),
			});
		},
		/** Group/line mutations mark dirty after applying result. */
		markDirtyAfterMutation: (state) => {
			state.changeTracking.isDirty = true;
			state.changeTracking.dirtyTimestamp = Date.now();
		},
		/** Save lifecycle: clear loading + dirty. */
		applySaveSuccess: (state) => {
			state.loading = false;
			state.changeTracking.isDirty = false;
			state.changeTracking.dirtyTimestamp = null;
		},
		/** Create-aufmass result. */
		applyCreateResult: (state, { payload }) => {
			state.creation.loading = false;
			state.creation.success = true;
			if (payload?.id) {
				state.formState.id = payload.id;
				state.changeTracking.isDirty = false;
				state.changeTracking.dirtyTimestamp = null;
			}
		},
		/** Update-aufmass result. */
		applyUpdateResult: (state) => {
			state.creation.loading = false;
			state.creation.success = true;
			state.changeTracking.isDirty = false;
			state.changeTracking.dirtyTimestamp = null;
		},
		/** fetchAufmassById result. */
		applyAufmassByIdResult: (state, { payload }) => {
			state.loading = false;
			state.formState = { ...state.formState, ...payload };
			state.changeTracking.isDirty = false;
			state.changeTracking.dirtyTimestamp = null;
		},
		/** Generic loading/error setters for mutation lifecycle. */
		setAufmassLoading: (state, { payload }) => {
			state.loading = Boolean(payload);
			if (payload) state.error = null;
		},
		setAufmassError: (state, { payload }) => {
			state.loading = false;
			state.error = payload;
		},
		setCreationPending: (state) => {
			state.creation.loading = true;
			state.creation.error = null;
			state.creation.success = false;
		},
		setCreationError: (state, { payload }) => {
			state.creation.loading = false;
			state.creation.error = payload;
			state.creation.success = false;
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch project aufmass list
			.addCase(fetchProjectAufmassListV2.pending, (state) => {
				state.loadingList = true;
				state.errorList = null;
			})
			.addCase(fetchProjectAufmassListV2.fulfilled, (state, action) => {
				state.loadingList = false;
				state.aufmassList = action.payload?.items || [];
			})
			.addCase(fetchProjectAufmassListV2.rejected, (state, action) => {
				state.loadingList = false;
				state.errorList = action.payload;
			})

			// Load aufmass data
			.addCase(loadAufmassDataV2.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(loadAufmassDataV2.fulfilled, (state, action) => {
				updateAufmassState(state, action);
				// Reset change tracking when loading fresh data
				state.changeTracking.isDirty = false;
				state.changeTracking.dirtyTimestamp = null;
			})
			.addCase(loadAufmassDataV2.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});
	},
});

// Actions
export const {
	setFormField,
	markFormDirty,
	markFormClean,
	updateFormFieldV2,
	setShowOnlyInvalidPositions,
	toggleExpandPosition,
	setAddLineModal,
	setAddGroupModal,
	setDeleteLineModal,
	setDeleteGroupModal,
	setAddedGroups,
	updateAddedLine,
	resetForm,
	expandAllPositions,
	collapseAllPositions,
	applyAufmassResultRaw,
	markDirtyAfterMutation,
	applySaveSuccess,
	applyCreateResult,
	applyUpdateResult,
	applyAufmassByIdResult,
	setAufmassLoading,
	setAufmassError,
	setCreationPending,
	setCreationError,
} = aufmassV2Slice.actions;

// Base selectors
export const selectAufmassV2State = (state) => state.aufmassV2;

// Form selectors
export const selectFormStateV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.formState || {},
);

export const selectFormDataV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.formState || {},
);

// Data selectors
export const selectInvoiceDataV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.invoiceData || {},
);

export const selectPositionsV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.positions || [],
);

export const selectAddedGroupsV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.addedGroups || {},
);

export const selectHierarchicalLVDataV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.hierarchicalLVData || [],
);

// UI state selectors
export const selectLoadingV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.loading || false,
);

export const selectReadonlyV2 = createSelector(
	[selectFormStateV2],
	(formState) => formState.readonly || false,
);

export const selectExpandedPositionsV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.expandedPositions || [],
);

// Creation state selectors
export const selectCreationStateV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.creation || {},
);

// Change tracking selectors
export const selectChangeTrackingV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.changeTracking || {},
);

// List selectors
export const selectAufmassListV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.aufmassList || [],
);

export const selectLoadingListV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.loadingList || false,
);

// Modal selectors
export const selectShowAddLineModalV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.showAddLineModal || false,
);

export const selectShowDeleteLineModalV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.showDeleteLineModal || false,
);

export const selectDeleteLineDataV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.deleteLineData,
);

export const selectPositionUnitMapV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.positionUnitMap || {},
);

export const selectCarriedForwardDataV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.carriedForwardData || {},
);

export const selectShowAddGroupModalV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.showAddGroupModal || false,
);

export const selectSelectedPositionV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.selectedPosition,
);

export const selectEditGroupDataV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.editGroupData,
);

export const selectEditingLineV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.editingLine,
);

// Error selectors
export const selectErrorV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.error,
);

export const selectErrorListV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.errorList,
);

// UI selectors
export const selectShowOnlyInvalidPositionsV2 = createSelector(
	[selectAufmassV2State],
	(aufmassV2) => aufmassV2.ui.showOnlyInvalidPositions,
);

export default aufmassV2Slice.reducer;
